import { useEffect, useRef } from 'react';
import { portalWs, type WidgetChannelEvents } from '../services/websocket';
import { env } from '../config/env';

interface UsePortalWebSocketOptions {
  customerId: number;
  wssSign: string;
  onCallEnd?: (data: WidgetChannelEvents['call_end']) => void;
  onBasketUpdated?: (data: WidgetChannelEvents['basket_products_updated']) => void;
  onWishlistUpdated?: (data: WidgetChannelEvents['wish_products_updated']) => void;
  onConnectionChange?: (connected: boolean) => void;
  onSellerAssigned?: (data: WidgetChannelEvents['seller_assigned']) => void;
  // W5: the seller moved between video / audio / chat. Informational — nothing
  // about this side's interface is decided by it.
  onParticipantModeChanged?: (data: WidgetChannelEvents['participant_mode_changed']) => void;
}

export function usePortalWebSocket(options: UsePortalWebSocketOptions) {
  const { customerId, wssSign, onCallEnd, onBasketUpdated, onWishlistUpdated, onConnectionChange, onSellerAssigned, onParticipantModeChanged } = options;

  // Store callbacks in refs so that changing them doesn't reconnect the WebSocket
  const callbacksRef = useRef({ onCallEnd, onBasketUpdated, onWishlistUpdated, onConnectionChange, onSellerAssigned, onParticipantModeChanged });
  callbacksRef.current = { onCallEnd, onBasketUpdated, onWishlistUpdated, onConnectionChange, onSellerAssigned, onParticipantModeChanged };

  useEffect(() => {
    // Skip WebSocket connection in demo mode (SPEC-030, AC-8)
    if (env.demoMode) {
      return;
    }

    portalWs.connect(customerId, wssSign);

    const unsubscribers: (() => void)[] = [];

    unsubscribers.push(portalWs.on('call_end', (data) => {
      callbacksRef.current.onCallEnd?.(data);
    }));
    unsubscribers.push(portalWs.on('basket_products_updated', (data) => {
      callbacksRef.current.onBasketUpdated?.(data);
    }));
    unsubscribers.push(portalWs.on('wish_products_updated', (data) => {
      callbacksRef.current.onWishlistUpdated?.(data);
    }));
    unsubscribers.push(portalWs.on('seller_assigned', (data) => {
      callbacksRef.current.onSellerAssigned?.(data);
    }));
    unsubscribers.push(portalWs.on('participant_mode_changed', (data) => {
      callbacksRef.current.onParticipantModeChanged?.(data);
    }));
    unsubscribers.push(portalWs.onConnectionChange((connected) => {
      callbacksRef.current.onConnectionChange?.(connected);
    }));

    return () => {
      unsubscribers.forEach(unsub => unsub());
      portalWs.disconnect();
    };
  }, [customerId, wssSign]);
}
